
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Phone, Mail, X, Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { trackPhoneCall, trackEvent } from "@/components/GoogleAnalytics";

interface StickyCTAProps {
  onClick: () => void;
}

const StickyCTA = ({ onClick }: StickyCTAProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const threshold = window.innerHeight * 0.5;
      setIsVisible(scrolled > threshold);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleQuickContact = async () => {
    setIsLoading(true);
    trackEvent('sticky_cta_clicked', {
      category: 'engagement',
      label: 'sticky_consultation_button'
    });
    
    // Simulate loading for better UX
    setTimeout(() => {
      setIsLoading(false);
      onClick();
    }, 500);
  };

  const handlePhoneClick = () => {
    trackPhoneCall();
    window.open('tel:+16167270343', '_self');
  };

  const handleEmailClick = () => {
    trackEvent('email_clicked', {
      category: 'conversion',
      label: 'sticky_email_button'
    });
    window.open('mailto:hello@organizebydesignmichigan.com', '_blank');
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 animate-fade-in">
      {/* Quick Contact Options */}
      <div className="bg-white/95 backdrop-blur border border-primary/20 rounded-lg p-3 shadow-lg">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-sm font-medium text-foreground">Quick Contact</div>
          <button 
            onClick={() => setIsVisible(false)}
            className="p-1 hover:bg-muted rounded"
            aria-label="Close quick contact"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
        
        <div className={`flex gap-2 ${isMobile ? 'flex-col' : 'flex-row'}`}>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 text-xs"
            onClick={handlePhoneClick}
          >
            <Phone className="w-3 h-3 mr-1" />
            Call
          </Button>
          
          <Button
            size="sm"
            variant="outline" 
            className="flex-1 text-xs"
            onClick={handleEmailClick}
          >
            <Mail className="w-3 h-3 mr-1" />
            Email
          </Button>
        </div>
      </div>

      {/* Main CTA Button */}
      <Button
        onClick={handleQuickContact}
        disabled={isLoading}
        className="luxury-gradient text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
        size={isMobile ? "default" : "lg"}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Loading...
          </>
        ) : (
          <>
            <Mail className="w-4 h-4 mr-2" />
            {isMobile ? "Get Quote" : "Free Consultation"}
          </>
        )}
      </Button>
    </div>
  );
};

export default StickyCTA;
